import { FetchApi } from '@backstage/frontend-plugin-api';
import { CalendarApi, CalendarEvent } from './types';

export class CalendarClient implements CalendarApi {
  // Using the recommended 'plugin://' protocol delegates base URL resolution (and Backstage auth middleware) to fetchApi
  private static readonly eventsUrl = 'plugin://community-calendar/events';
  private readonly fetchApi: FetchApi;

  constructor(options: { fetchApi: FetchApi }) {
    this.fetchApi = options.fetchApi;
  }

  async getEvents(): Promise<CalendarEvent[]> {
    const response = await this.fetchApi.fetch(CalendarClient.eventsUrl, {
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch events: ${response.statusText}`);
    }

    const data = await response.json();
    return data.events;
  }

  async getEvent(eventId: string): Promise<CalendarEvent | null> {
    const eventUrl = `${CalendarClient.eventsUrl}/${eventId}`;

    const response = await this.fetchApi.fetch(eventUrl, {
      headers: await this.getAuthHeaders(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch event: ${response.statusText}`);
    }

    return await response.json();
  }

  async joinEvent(eventId: string): Promise<void> {
    const joinEventUrl = `${CalendarClient.eventsUrl}/${eventId}/join`;

    const response = await this.fetchApi.fetch(joinEventUrl, {
      method: 'PATCH',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to join event: ${response.statusText}`);
    }
  }

  private async getAuthHeaders(): Promise<HeadersInit | undefined> {
    const msToken = await this.getMicrosoftToken();
    return {
      'X-MS-Token': msToken,
    };
  }

  private async getMicrosoftToken(): Promise<string> {
    const response = await this.fetchApi.fetch('plugin://auth/microsoft/refresh?env=development', {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to refresh Microsoft token: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    const graphToken = data?.providerInfo?.accessToken;

    if (!graphToken || typeof graphToken !== 'string') {
      throw new Error('Microsoft refresh response missing providerInfo.accessToken');
    }

    return graphToken;
  }
}
